import { Slide } from '@features/powerpoint';
import { BuildReportOptions } from '../../utils/build-presentation';
import { dimensionSlide } from '@features/executive-report/slides/v2/dimension';
import { formatters } from '@utils/formattersV2';
import { formatWithSign } from '@features/executive-report/utils/format-with-sign';
import { themeV2 } from '@features/executive-report/domain/theme-v2';
import { FormSchema } from '../../form-schema';
import { buildNavbarText } from '@features/executive-report/slides/v2/components/navbar-texts';
import { navbarSections } from '../../domain/navbar-sections';
import { visibleSlide } from '@features/executive-report/slides';
import { TOTAL_DIMENSIONS } from '@features/executive-report/dimensions/layout';

type BaseDimensionObject = {
  dimensionValue: string;
};

type BuildDimensionSlideProps<T extends BaseDimensionObject> = {
  navbarSection: (typeof navbarSections)[number];
  dimensionName: string;
  dimensionSlug: string;
  headerPrefix: string;
  dimensions: readonly T[];
  overallMetrics: Parameters<typeof dimensionSlide>[0]['overallMetrics'];
  subdimensionImages: FormSchema['subdimensionImagesByNameAndValue'];
  getDimensionMetrics: (
    dimension: T
  ) => Parameters<
    typeof dimensionSlide
  >[0]['dimensionMetrics'][number]['metrics'];
  getPlaceholder?: (dimension: T) => string;
};

const getNormalDimensionMetrics: BuildDimensionSlideProps<
  NonNullable<BuildReportOptions['topSalesSubdimensionsByName']['ad']>[number]
>['getDimensionMetrics'] = dimension => [
  {
    label: 'Sales Lift',
    value: formatWithSign(dimension.incrementalSalesLift, formatters.lift),
  },
  {
    label: 'Incremental Sales',
    value: formatters.currency(dimension.projectedIncrementalSales, {
      full: true,
      decimals: 0,
    }),
  },
  {
    label: 'Impressions',
    value: formatters.impressions(dimension.impressions, {
      full: true,
    }),
  },
];

const getRetailerDimensionMetrics: BuildDimensionSlideProps<
  BuildReportOptions['topRetailers'][number] & BaseDimensionObject
>['getDimensionMetrics'] = retailer => [
  {
    label: 'Total Sales',
    value: formatters.currency(retailer.totalProjectedSales),
  },
  {
    label: 'Incremental Sales',
    value: formatters.currency(retailer.projectedIncrementalSales, {
      full: true,
      decimals: 0,
    }),
  },
  {
    label: 'Sales Lift',
    value: formatWithSign(retailer.incrementalSalesLift, formatters.lift),
  },
];

const buildDimensionSlide = <T extends BaseDimensionObject>(
  props: BuildDimensionSlideProps<T>
): Slide => {
  if (props.dimensions.length === 0) return {};

  return dimensionSlide({
    navbarText: buildNavbarText(navbarSections, props.navbarSection),
    dimensionName: props.dimensionName,
    overallMetrics: props.overallMetrics,
    dimensionMetrics: props.dimensions
      .slice(0, TOTAL_DIMENSIONS)
      .map(
        (
          subdimension
        ): Parameters<
          typeof dimensionSlide
        >[0]['dimensionMetrics'][number] => ({
          dimension: subdimension.dimensionValue,
          placeholder: props.getPlaceholder?.(subdimension),
          image:
            props.subdimensionImages[props.dimensionSlug]?.[
              subdimension.dimensionValue
            ] ?? null,
          metrics: props.getDimensionMetrics(subdimension),
        })
      ),
    headerText: {
      text: [
        {
          text: `${props.headerPrefix}: `,
          options: {
            color: themeV2.colors.inmarketGreen,
          },
        },
        {
          text: `${props.dimensionName}s`,
        },
      ],
      options: {
        fontSize: 36,
        bold: true,
        h: 0.75,
        w: 12.67,
        x: 0.33,
        y: 0.59,
      },
    },
  });
};

export const salesDimensionSlides = (props: BuildReportOptions): Slide[] => {
  const overallMetrics: Parameters<typeof dimensionSlide>[0]['overallMetrics'] =
    [
      {
        label: 'Incremental Sales and Lift',
        value: formatters.currency(props.report.projectedIncrementalSales, {
          decimals: 1,
        }),
        lift: formatWithSign(
          props.report.incrementalSalesLift,
          formatters.lift
        ),
      },
      {
        label: 'Brand Transaction Size',
        value: formatters.currency(props.report.averagePricePerTransaction, {
          decimals: 2,
        }),
      },
      {
        label: 'Basket Size',
        value: formatters.currency(props.report.averageTotalBasketAmount, {
          decimals: 2,
        }),
      },
      {
        label: 'Purchase Frequency',
        value: `${formatters.number(props.report.averagePurchaseFrequency, {
          decimals: 1,
        })}x`,
      },
    ];

  return [
    ...visibleSlide({
      slide: buildDimensionSlide({
        navbarSection: 'The Who',
        dimensionName: 'Audience',
        dimensionSlug: 'aud',
        headerPrefix: 'The WHO',
        dimensions: props.topSalesSubdimensionsByName.aud ?? [],
        overallMetrics,
        subdimensionImages: props.customFields.subdimensionImagesByNameAndValue,
        getDimensionMetrics: getNormalDimensionMetrics,
      }),
      visible:
        props.customFields.includedSlides.sales.who &&
        (props.topSalesSubdimensionsByName.aud?.length ?? 0) > 0,
    }),
    ...visibleSlide({
      slide: buildDimensionSlide({
        navbarSection: 'The What',
        dimensionName: 'Creative',
        dimensionSlug: 'creative',
        headerPrefix: 'The WHAT',
        dimensions: props.topSalesSubdimensionsByName.creative ?? [],
        overallMetrics,
        subdimensionImages: props.customFields.subdimensionImagesByNameAndValue,
        getDimensionMetrics: getNormalDimensionMetrics,
      }),
      visible:
        props.customFields.includedSlides.sales.what &&
        (props.topSalesSubdimensionsByName.creative?.length ?? 0) > 0,
    }),
    ...visibleSlide({
      slide: buildDimensionSlide({
        navbarSection: 'The Where',
        dimensionName: 'Channel',
        dimensionSlug: 'channel',
        headerPrefix: 'The WHERE',
        dimensions: props.topSalesSubdimensionsByName.channel ?? [],
        overallMetrics,
        subdimensionImages: props.customFields.subdimensionImagesByNameAndValue,
        getDimensionMetrics: getNormalDimensionMetrics,
      }),
      visible:
        props.customFields.includedSlides.sales.where &&
        (props.topSalesSubdimensionsByName.channel?.length ?? 0) > 0,
    }),
    ...visibleSlide({
      slide: buildDimensionSlide({
        navbarSection: 'The How',
        dimensionName: 'Retailer',
        dimensionSlug: 'retailer',
        headerPrefix: 'The HOW',
        dimensions: props.topRetailers.map(retailer => ({
          dimensionValue: retailer.name,
          ...retailer,
        })),
        overallMetrics,
        subdimensionImages: props.customFields.subdimensionImagesByNameAndValue,
        getDimensionMetrics: getRetailerDimensionMetrics,
        getPlaceholder: retailer => retailer.name,
      }),
      visible:
        props.customFields.includedSlides.sales.how &&
        props.topRetailers.length > 0,
    }),
  ];
};
